<template>
  <SectionContentRedFlagContent v-bind="props" />
</template>

<script setup>
const props = defineProps({
  badgeNumber: {
    type: String,
    default: '1'
  },
  badgeText: {
    type: String,
    default: 'Red Flag No. 1'
  },
  title: {
    type: String,
    default: "Why Heart Health Is the #1 Predictor of How You'll Age"
  },
  introContent: {
    type: String,
    default: `<p>Men are significantly more likely to experience cardiovascular issues earlier in life. One in four American men will die from heart disease.</p>
<p>And the first heart attack often strikes a decade earlier than it does in women.</p>
<p>But research shows something remarkable…</p>`
  },
  stainImageSrc: {
    type: String,
    default: ''
  },
  calloutIcon: {
    type: String,
    default: 'coffee'
  },
  calloutTitle: {
    type: String,
    default: 'Is Coffee Helping or Hurting Your Heart?'
  },
  calloutContent: {
    type: String,
    default: `<p>Studies have shown men who drink clean, antioxidant-rich coffee daily have lower risks of:</p>
<ul>
  <li>Hypertension</li>
  <li>Heart failure</li>
  <li>Atrial fibrillation</li>
  <li>Cardiovascular-related mortality</li>
</ul>
<p>A 2021 study published in Circulation: Heart Failure found that drinking 1–2 cups of coffee per day was associated with a <strong>5–12% decreased risk of heart failure.</strong> Another large review in The American Journal of Clinical Nutrition concluded that moderate coffee consumption is linked to <strong>lower all-cause mortality</strong> and improved cardiovascular outcomes.</p>`
  },
  calloutAsideImageSrc: {
    type: String,
    default: ''
  },
  calloutAsideImageAlt: {
    type: String,
    default: ''
  },
  showCalloutAsidePlaceholder: {
    type: Boolean,
    default: false
  },
  outroContent: {
    type: String,
    default: `<p>One reason coffee is such a powerful tool in your total body health arsenal is due to its antioxidant content which can aid in lowering inflammation (a driver of heart disease) and reducing oxidative stress.<sup>8</sup></p>
<p>Of course, not all coffee is the same. That's why sourcing and testing matter. Some coffees may be exposed to unwanted chemicals during cultivation and processing, while others prioritize cleaner growing practices and rigorous quality standards.</p>
<p>Lifeboost Coffee's beans are single-origin, shade-grown, and tested for over 400 mycotoxins, heavy metals, and other contaminants.</p>
<p>That means every cup is crafted to deliver antioxidants without the same chemical burden found in conventional coffee.</p>
<p>For men, that matters a lot.</p>`
  },
  showDivider: {
    type: Boolean,
    default: true
  },
  paddingBottom: {
    type: String,
    default: 'default'
  },
  paddingTop: {
    type: String,
    default: 'default'
  }
})

defineBuilderComponent({
  friendlyName: 'Content - Red Flag Heart Health',
  inputs: [
    {
      name: 'badgeNumber',
      type: 'text',
      defaultValue: '1'
    },
    {
      name: 'badgeText',
      type: 'text',
      defaultValue: 'Red Flag No. 1'
    },
    {
      name: 'title',
      type: 'richText',
      defaultValue: "Why Heart Health Is the #1 Predictor of How You'll Age"
    },
    {
      name: 'introContent',
      type: 'richText',
      defaultValue: `<p>Men are significantly more likely to experience cardiovascular issues earlier in life. One in four American men will die from heart disease.</p>
<p>And the first heart attack often strikes a decade earlier than it does in women.</p>
<p>But research shows something remarkable…</p>`
    },
    {
      name: 'stainImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
      defaultValue: ''
    },
    {
      name: 'calloutIcon',
      type: 'text',
      enum: ['', 'coffee', 'alert'],
      defaultValue: 'coffee'
    },
    {
      name: 'calloutTitle',
      type: 'text',
      defaultValue: 'Is Coffee Helping or Hurting Your Heart?'
    },
    {
      name: 'calloutContent',
      type: 'richText',
      defaultValue: `<p>Studies have shown men who drink clean, antioxidant-rich coffee daily have lower risks of:</p>
<ul>
  <li>Hypertension</li>
  <li>Heart failure</li>
  <li>Atrial fibrillation</li>
  <li>Cardiovascular-related mortality</li>
</ul>
<p>A 2021 study published in Circulation: Heart Failure found that drinking 1–2 cups of coffee per day was associated with a <strong>5–12% decreased risk of heart failure.</strong> Another large review in The American Journal of Clinical Nutrition concluded that moderate coffee consumption is linked to <strong>lower all-cause mortality</strong> and improved cardiovascular outcomes.</p>`
    },
    {
      name: 'calloutAsideImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
      defaultValue: ''
    },
    {
      name: 'calloutAsideImageAlt',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'showCalloutAsidePlaceholder',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'outroContent',
      type: 'richText',
      defaultValue: `<p>One reason coffee is such a powerful tool in your total body health arsenal is due to its antioxidant content which can aid in lowering inflammation (a driver of heart disease) and reducing oxidative stress.<sup>8</sup></p>
<p>Of course, not all coffee is the same. That's why sourcing and testing matter. Some coffees may be exposed to unwanted chemicals during cultivation and processing, while others prioritize cleaner growing practices and rigorous quality standards.</p>
<p>Lifeboost Coffee's beans are single-origin, shade-grown, and tested for over 400 mycotoxins, heavy metals, and other contaminants.</p>
<p>That means every cup is crafted to deliver antioxidants without the same chemical burden found in conventional coffee.</p>
<p>For men, that matters a lot.</p>`
    },
    {
      name: 'showDivider',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'paddingBottom',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'default'
    },
    {
      name: 'paddingTop',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'default'
    }
  ]
})
</script>
